import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { api, setBootstrapCsrf } from "@/lib/api";

export type Role = "ADMIN" | "MANAGER" | "STAFF";

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: Role;
  memberships: Array<{ locationId: number; scope: "READ" | "MANAGE" }>;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (...roles: Role[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api.getRaw<{ user: User; csrfToken: string }>("/api/v1/auth/me");
      setBootstrapCsrf(me.csrfToken);
      setUser(me.user);
    } catch {
      setBootstrapCsrf(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isPublicRoute(location.pathname)) {
      setLoading(false);
      return;
    }
    void refresh();
  }, [location.pathname, refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.postRaw<{ user: User; csrfToken: string }>("/api/v1/auth/login", {
      email,
      password,
    });
    setBootstrapCsrf(res.csrfToken);
    setUser(res.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/v1/auth/logout");
    } catch {
      /* even on failure, clear local state */
    }
    setBootstrapCsrf(null);
    setUser(null);
  }, []);

  const hasRole = useCallback(
    (...roles: Role[]) => !!user && roles.includes(user.role),
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, logout, hasRole }),
    [user, loading, login, logout, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function isPublicRoute(pathname: string): boolean {
  return pathname === "/login"
    || pathname === "/book"
    || pathname === "/help"
    || pathname.startsWith("/help/")
    || pathname.startsWith("/manage/");
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
